import React from "react";
import { useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Building2, Calendar, Clock, ArrowRight, TrendingUp, Shield, Users } from "lucide-react";
import Header from "@/components/Header";

const BLOG_POSTS = [
  {
    id: 1,
    title: "5 Ways Professional Property Management Maximizes Your Rental Income",
    excerpt: "Discover how hiring a professional property manager can increase your monthly rental income by 15-25% through strategic pricing, reduced vacancy rates, and expert tenant screening.",
    content: `
      <h2>Introduction</h2>
      <p>Many property owners hesitate to hire a property management company due to the management fees. However, professional property managers often increase your net income significantly through proven strategies and expertise.</p>
      
      <h2>1. Strategic Rental Pricing</h2>
      <p>Property managers use market analysis tools and local expertise to set optimal rental rates. They track market trends, comparable properties, and seasonal demand to ensure you're maximizing income without pricing yourself out of the market.</p>
      <p><strong>Impact:</strong> Proper pricing can increase your rental income by 10-15% compared to owner-managed properties that are often underpriced due to lack of market knowledge.</p>
      
      <h2>2. Reduced Vacancy Rates</h2>
      <p>Professional managers have established marketing channels, listing optimization expertise, and quick response times that reduce vacancy periods. They know where to advertise, how to photograph properties, and how to screen applicants efficiently.</p>
      <p><strong>Impact:</strong> Reducing vacancy from 2 months per year to 2 weeks saves you approximately 12% of annual rental income.</p>
      
      <h2>3. Expert Tenant Screening</h2>
      <p>Quality tenants pay on time, take care of your property, and stay longer. Property managers conduct thorough background checks, credit checks, employment verification, and reference checks that owner-landlords often skip or do inadequately.</p>
      <p><strong>Impact:</strong> Better tenants mean fewer late payments, reduced property damage, and longer tenancies - saving thousands in turnover costs.</p>
      
      <h2>4. Preventive Maintenance Programs</h2>
      <p>Regular maintenance inspections and preventive repairs catch small issues before they become expensive problems. Professional managers have relationships with reliable contractors who offer competitive rates.</p>
      <p><strong>Impact:</strong> Preventing major repairs can save $5,000-$15,000 annually on emergency fixes and extends your property's value.</p>
      
      <h2>5. Legal Compliance & Risk Management</h2>
      <p>Property managers stay current with landlord-tenant laws, safety regulations, and required disclosures. They handle evictions professionally and know how to navigate legal complexities.</p>
      <p><strong>Impact:</strong> Avoiding even one lawsuit or wrongful eviction claim can save $10,000-$50,000 in legal fees and lost income.</p>
      
      <h2>The Bottom Line</h2>
      <p>While property management fees typically range from 8-12% of monthly rent, the value they provide often results in 15-25% higher net income through optimized pricing, reduced vacancies, better tenants, lower maintenance costs, and legal protection.</p>
      
      <p><strong>Ready to maximize your rental income?</strong> Connect with professional property managers in your area through Top Prop and see how much more you could be earning.</p>
    `,
    category: "Income & ROI",
    readTime: "8 min read",
    date: "November 17, 2025",
    icon: TrendingUp
  },
  {
    id: 2,
    title: "How Property Managers Handle Difficult Tenants (So You Don't Have To)",
    excerpt: "Learn the professional strategies property managers use to handle late payments, property damage, noise complaints, and evictions - protecting your investment and your peace of mind.",
    content: `
      <h2>The Reality of Tenant Issues</h2>
      <p>Every landlord will eventually face challenging tenant situations. Whether it's late rent, property damage, neighbor complaints, or the dreaded eviction process, these issues can be stressful, time-consuming, and costly if not handled properly.</p>
      
      <h2>1. Late Rent Payments</h2>
      <p><strong>The Amateur Approach:</strong> Many DIY landlords struggle with awkward conversations, inconsistent enforcement, and emotional decision-making when tenants miss payments.</p>
      <p><strong>The Professional Approach:</strong> Property managers follow established protocols:</p>
      <ul>
        <li>Automated payment reminders before due dates</li>
        <li>Immediate late notices on day one (as required by law)</li>
        <li>Clear escalation procedures with specific timelines</li>
        <li>Professional, documented communication at every step</li>
        <li>Quick action on chronic late payers</li>
      </ul>
      <p>This systematic approach reduces late payments by 70% compared to owner-managed properties.</p>
      
      <h2>2. Property Damage</h2>
      <p><strong>What Property Managers Do:</strong></p>
      <ul>
        <li>Conduct thorough move-in/move-out inspections with photo documentation</li>
        <li>Regular property inspections (quarterly or semi-annually) to catch issues early</li>
        <li>Clear damage protocols in lease agreements</li>
        <li>Professional assessment of security deposit deductions</li>
        <li>Quick repair coordination to prevent further damage</li>
      </ul>
      <p>Early detection through regular inspections prevents 80% of major property damage.</p>
      
      <h2>3. Noise and Neighbor Complaints</h2>
      <p>Property managers act as neutral mediators, investigating complaints objectively and taking measured action. They know how to distinguish between legitimate issues and personality conflicts, and they have experience resolving disputes without escalating tensions.</p>
      <p><strong>Key Strategies:</strong></p>
      <ul>
        <li>Document all complaints with dates and details</li>
        <li>Issue formal warnings when appropriate</li>
        <li>Provide tenants with specific expectations and consequences</li>
        <li>Know when to involve authorities or legal counsel</li>
      </ul>
      
      <h2>4. The Eviction Process</h2>
      <p>This is where professional management truly shines. Evictions are legally complex, emotionally charged, and can cost $5,000-$10,000 if handled incorrectly.</p>
      <p><strong>What Property Managers Handle:</strong></p>
      <ul>
        <li>Ensure all legal requirements are met before starting eviction</li>
        <li>File proper notices and court documents</li>
        <li>Attend court hearings on your behalf</li>
        <li>Coordinate with sheriff's office for tenant removal</li>
        <li>Handle property turnover and re-rental</li>
      </ul>
      <p>Professional managers complete evictions 60% faster than DIY landlords and win 95% of cases due to proper documentation.</p>
      
      <h2>5. Lease Violations</h2>
      <p>From unauthorized occupants to prohibited pets, lease violations require careful handling. Property managers enforce lease terms consistently while maintaining positive tenant relationships when possible.</p>
      
      <h2>The Emotional Buffer</h2>
      <p>Perhaps the biggest benefit: property managers serve as an emotional buffer between you and your tenants. They can make tough decisions without guilt, enforce rules without friendship complications, and handle confrontations professionally.</p>
      
      <h2>Your Time and Stress</h2>
      <p>Handling difficult tenants can consume 20-40 hours per incident. Late-night calls, emergency repairs, court appearances, and endless texts take a toll. Property managers handle all of this while you focus on your career and family.</p>
      
      <p><strong>Bottom Line:</strong> A property manager's expertise in handling difficult tenants protects your income, preserves your property, and saves your sanity. The peace of mind alone is worth the management fee.</p>
      
      <p>Find experienced property managers who know how to handle even the toughest situations - connect with professionals through Top Prop today.</p>
    `,
    category: "Tenant Management",
    readTime: "10 min read",
    date: "November 15, 2025",
    icon: Shield
  },
  {
    id: 3,
    title: "First-Time Landlord? Why Hiring a Property Manager Is Your Best Decision",
    excerpt: "New to being a landlord? Discover why partnering with a professional property manager from day one can save you thousands in mistakes and set you up for long-term success.",
    content: `
      <h2>The First-Time Landlord Challenge</h2>
      <p>Congratulations on your first rental property! Whether you inherited it, bought it as an investment, or moved and decided to rent your old home, you're about to enter the world of being a landlord. While DIY management might seem cost-effective, first-time landlords often learn expensive lessons the hard way.</p>
      
      <h2>The Hidden Costs of DIY Management</h2>
      <p>First-time landlords typically face:</p>
      <ul>
        <li><strong>Legal Mistakes:</strong> One improper eviction or discrimination claim can cost $10,000-$50,000</li>
        <li><strong>Bad Tenant Selection:</strong> A problematic tenant costs an average of $7,000 in lost rent and damages</li>
        <li><strong>Underpricing:</strong> Setting rent too low costs $200-$500 monthly (that's $2,400-$6,000 annually!)</li>
        <li><strong>Delayed Maintenance:</strong> Small issues become expensive repairs, averaging $3,000-$8,000 in preventable costs</li>
        <li><strong>Extended Vacancies:</strong> Poor marketing adds 30-60 days of vacancy, costing $2,000-$4,000</li>
      </ul>
      <p><strong>Total potential first-year losses: $14,000-$75,000</strong></p>
      
      <h2>What You Don't Know (But Your Property Manager Does)</h2>
      
      <h3>Legal Compliance</h3>
      <p>Rental housing laws in Canada are complex and vary by province. Property managers know:</p>
      <ul>
        <li>Required safety standards and inspections</li>
        <li>Proper lease agreement clauses</li>
        <li>Rent increase limitations and notice requirements</li>
        <li>Security deposit rules</li>
        <li>Eviction procedures and grounds</li>
        <li>Discrimination laws and fair housing requirements</li>
        <li>Required disclosures and notices</li>
      </ul>
      
      <h3>Tenant Screening</h3>
      <p>This is where first-time landlords make the most costly mistakes. Professional screening includes:</p>
      <ul>
        <li>Credit checks (knowing which red flags matter)</li>
        <li>Criminal background checks</li>
        <li>Employment verification</li>
        <li>Previous landlord references (and knowing how to verify they're real)</li>
        <li>Income verification (2.5-3x rent is standard)</li>
        <li>Eviction history checks</li>
      </ul>
      <p>One bad tenant can undo years of rental income. Property managers have refined systems to identify quality tenants.</p>
      
      <h3>Pricing Strategy</h3>
      <p>First-time landlords often:</p>
      <ul>
        <li>Undervalue their property due to emotional attachment or lack of market knowledge</li>
        <li>Overprice and sit vacant for months</li>
        <li>Miss seasonal pricing opportunities</li>
        <li>Don't account for property features that command premium rent</li>
      </ul>
      <p>Property managers use comparative market analysis, historical data, and current market trends to optimize your rental rate.</p>
      
      <h2>The Learning Curve Tax</h2>
      <p>Every experienced landlord has war stories about their first tenant or first eviction. These lessons typically cost thousands. Why not skip the learning curve and benefit from someone else's 10+ years of experience from day one?</p>
      
      <h2>Your Time is Valuable</h2>
      <p>As a first-time landlord, you'll spend:</p>
      <ul>
        <li>20-30 hours learning landlord laws</li>
        <li>15-25 hours marketing and showing the property</li>
        <li>10-15 hours screening applicants</li>
        <li>5-10 hours on lease preparation</li>
        <li>10-20 hours monthly on tenant communication and maintenance coordination</li>
      </ul>
      <p>That's 60-100 hours upfront, plus 10-20 hours monthly. At even $25/hour, that's $4,500-$9,500 in time cost annually.</p>
      
      <h2>Building Good Habits from the Start</h2>
      <p>Working with a property manager from day one teaches you what professional management looks like:</p>
      <ul>
        <li>Professional communication standards</li>
        <li>Proper documentation practices</li>
        <li>Maintenance protocols</li>
        <li>Financial tracking and reporting</li>
        <li>Legal compliance procedures</li>
      </ul>
      <p>If you ever decide to self-manage later, you'll be much better prepared.</p>
      
      <h2>The Peace of Mind Factor</h2>
      <p>Being a first-time landlord is stressful enough without:</p>
      <ul>
        <li>3 AM emergency calls about burst pipes</li>
        <li>Wondering if you're violating any laws</li>
        <li>Awkward confrontations about late rent</li>
        <li>Coordinating repairs while working full-time</li>
        <li>Worrying about tenant screening mistakes</li>
      </ul>
      <p>Property managers handle all of this, letting you sleep at night.</p>
      
      <h2>When DIY Makes Sense</h2>
      <p>Honestly? For most first-time landlords, it doesn't. DIY might work if:</p>
      <ul>
        <li>You have significant time to dedicate (20+ hours monthly)</li>
        <li>You're detail-oriented and enjoy learning complex regulations</li>
        <li>You have experience in real estate or property management</li>
        <li>You live very close to the rental property</li>
        <li>You have a strong stomach for confrontation and legal issues</li>
      </ul>
      <p>Even then, most experienced landlords recommend professional management for your first property.</p>
      
      <h2>The Real Cost Comparison</h2>
      <p><strong>DIY Management:</strong></p>
      <ul>
        <li>$0 management fees</li>
        <li>$3,000-$10,000 in likely first-year mistakes</li>
        <li>200+ hours of your time ($5,000-$10,000 value)</li>
        <li>Significant stress and learning curve</li>
      </ul>
      
      <p><strong>Professional Management:</strong></p>
      <ul>
        <li>8-12% of monthly rent in management fees ($150-$250/month typical)</li>
        <li>Minimal mistakes (managers carry insurance for rare errors)</li>
        <li>5-10 hours of your time annually</li>
        <li>Peace of mind and professional expertise</li>
      </ul>
      
      <h2>Making the Right Choice</h2>
      <p>The management fee (typically $1,800-$3,000 annually) is easily offset by:</p>
      <ul>
        <li>Avoiding just one costly mistake</li>
        <li>Optimal pricing (worth $2,400-$6,000 annually)</li>
        <li>Reduced vacancy (worth $1,000-$3,000)</li>
        <li>Better tenant selection (priceless)</li>
        <li>Your time value (worth $5,000-$10,000)</li>
      </ul>
      
      <p><strong>Conclusion:</strong> For first-time landlords, professional property management isn't an expense - it's an insurance policy, a time-saver, and an education all rolled into one. Start your landlord journey on the right foot.</p>
      
      <p>Ready to find the right property manager for your first rental property? Top Prop connects you with experienced professionals who specialize in helping first-time landlords succeed.</p>
    `,
    category: "First-Time Landlords",
    readTime: "12 min read",
    date: "November 12, 2025",
    icon: Users
  }
];

export default function BlogPage() {
  const navigate = useNavigate();
  const [selectedPost, setSelectedPost] = React.useState(null);

  if (selectedPost) {
    const post = BLOG_POSTS.find(p => p.id === selectedPost);
    return (
      <div className="min-h-screen" style={{ backgroundColor: '#F7F7F7' }}>
        <Header variant="full" />
        
        <article className="container mx-auto px-4 sm:px-6 py-12 max-w-4xl">
          <button
            onClick={() => setSelectedPost(null)}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6"
          >
            ← Back to Blog
          </button>
          
          <div className="bg-white rounded-lg shadow-lg p-8">
            <div className="flex items-center gap-4 mb-4">
              <post.icon className="h-12 w-12" style={{ color: '#3b82f6' }} />
              <span className="px-3 py-1 rounded-full text-sm font-semibold" style={{ backgroundColor: '#e0f2fe', color: '#1e40af' }}>
                {post.category}
              </span>
            </div>
            
            <h1 className="text-4xl font-bold mb-4" style={{ color: '#1E1E1E' }}>
              {post.title}
            </h1>
            
            <div className="flex items-center gap-6 text-gray-600 mb-8 pb-8 border-b">
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                <span className="text-sm">{post.date}</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4" />
                <span className="text-sm">{post.readTime}</span>
              </div>
            </div>
            
            <div 
              className="prose prose-lg max-w-none"
              style={{ color: '#374151' }}
              dangerouslySetInnerHTML={{ __html: post.content }}
            />
            
            <div className="mt-12 pt-8 border-t">
              <div className="bg-gradient-to-r p-8 rounded-lg text-center" style={{ background: 'linear-gradient(90deg, #3b82f6 0%, #2563eb 100%)' }}>
                <h3 className="text-2xl font-bold text-white mb-4">
                  Ready to Find Professional Property Management?
                </h3>
                <p className="text-blue-100 mb-6">
                  Connect with vetted property managers in your area
                </p>
                <button
                  onClick={() => navigate("/get-matched")}
                  className="bg-white px-8 py-4 rounded-lg font-bold transition-all hover:shadow-xl"
                  style={{ color: '#3b82f6' }}
                >
                  Get Matched Now
                </button>
              </div>
            </div>
          </div>
        </article>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#F7F7F7' }}>
      <Header variant="full" />

      {/* Hero Section */}
      <section className="text-white py-16" style={{ background: 'linear-gradient(90deg, #3b82f6 0%, #2563eb 100%)' }}>
        <div className="container mx-auto px-4 sm:px-6">
          <div className="max-w-3xl">
            <h1 className="text-4xl sm:text-5xl font-bold mb-4">
              Property Management Insights & Guides
            </h1>
            <p className="text-xl text-blue-100">
              Expert advice and comprehensive guides to help property owners succeed with professional management.
            </p>
          </div>
        </div>
      </section>

      {/* Blog Posts Grid */}
      <section className="container mx-auto px-4 sm:px-6 py-12">
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
          {BLOG_POSTS.map((post) => (
            <Card 
              key={post.id} 
              className="hover:shadow-xl transition-all cursor-pointer bg-white rounded-lg overflow-hidden"
              onClick={() => setSelectedPost(post.id)}
            >
              <div className="h-48 flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #e0f2fe 0%, #bfdbfe 100%)' }}>
                <post.icon className="h-20 w-20" style={{ color: '#3b82f6', opacity: 0.5 }} />
              </div>
              <div className="p-6">
                <div className="flex items-center gap-4 text-sm text-slate-500 mb-3">
                  <span className="px-3 py-1 rounded-full text-xs font-semibold" style={{ backgroundColor: '#e0f2fe', color: '#1e40af' }}>
                    {post.category}
                  </span>
                  <div className="flex items-center gap-1">
                    <Clock className="h-4 w-4" />
                    {post.readTime}
                  </div>
                </div>
                <h3 className="text-xl font-bold mb-3 hover:text-blue-600 transition-colors" style={{ color: '#1E1E1E' }}>
                  {post.title}
                </h3>
                <p className="text-slate-600 mb-4 text-sm leading-relaxed">{post.excerpt}</p>
                <div className="flex items-center justify-between pt-3 border-t" style={{ borderColor: '#e5e7eb' }}>
                  <div className="flex items-center gap-2 text-xs text-slate-500">
                    <Calendar className="h-4 w-4" />
                    {post.date}
                  </div>
                  <div className="flex items-center gap-1 text-sm font-semibold" style={{ color: '#3b82f6' }}>
                    Read More <ArrowRight className="h-4 w-4" />
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      </section>

      {/* CTA Section */}
      <section className="text-white py-16" style={{ background: 'linear-gradient(90deg, #3b82f6 0%, #2563eb 100%)' }}>
        <div className="container mx-auto px-4 sm:px-6 text-center">
          <h2 className="text-3xl font-bold mb-4">Ready to Find Your Property Manager?</h2>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            Get matched with vetted property management companies in your area - free and fast.
          </p>
          <button
            onClick={() => navigate("/get-matched")}
            className="bg-white font-bold text-lg px-8 py-4 rounded-lg hover:shadow-xl transition-all"
            style={{ color: '#3b82f6' }}
          >
            Get Matched Now
          </button>
        </div>
      </section>
    </div>
  );
}
